import { get, pick } from 'lodash';
import Raven from 'raven-js';

import { defaultPristineValues } from 'utils/questionHelpers';
import { adjustAudienceQuotaPercentages } from 'utils/quotaHelpers';
import { setupCriterion } from './Criteria/helpers';
import {
  SURVEY_TYPE_ENUM,
  initialCriteria,
  residentialPostalCodesCriterionKey,
  geolocationCriteriaKeysWithoutPostalCode,
} from './constants';
import {
  getScreeningQuestionsAfterAdd,
  getScreeningQuestionsAfterRemove,
  getScreeningQuestionsAfterUpdate,
} from './helpers';

function addScreeningQuestionActionHandler(state, action) {
  const newVersion = state.targetingEditVersion + 1;
  const newScreeningQuestions = getScreeningQuestionsAfterAdd(
    state.screeningQuestions,
    action.payload,
  );
  const activeScreeningQuestionIds = newScreeningQuestions.map(
    (question) => `sq-${question.refId}`,
  );
  const errors = state.errors.filter(
    ({ key, category }) =>
      activeScreeningQuestionIds.includes(key) ||
      category === 'criteria' ||
      category === 'audience',
  );

  return Object.assign({}, state, {
    errors,
    screeningQuestions: newScreeningQuestions,
    targetingEditVersion: newVersion,
  });
}

function duplicateMultipleScreeningQuestionsHandler(state, action) {
  const { questions, originQuestionRefId } = action.payload;

  let newState = state;
  questions.forEach((question) => {
    newState = addScreeningQuestionActionHandler(newState, {
      payload: {
        newScreeningQuestion: question,
        insertAfterQuestionRefId: originQuestionRefId,
      },
    });
  });
  return newState;
}

function removeScreeningQuestionActionHandler(state, action) {
  const newVersion = state.targetingEditVersion + 1;
  const newScreeningQuestions = getScreeningQuestionsAfterRemove(
    state.screeningQuestions,
    action.payload.refId,
  );

  const activeScreeningQuestionIds = newScreeningQuestions.map(
    (question) => `sq-${question.refId}`,
  );
  const errors = state.errors.filter(
    ({ key, category }) =>
      activeScreeningQuestionIds.includes(key) ||
      category === 'criteria' ||
      category === 'audience',
  );

  return Object.assign({}, state, {
    errors,
    screeningQuestions: newScreeningQuestions,
    targetingEditVersion: newVersion,
  });
}

function removeAllScreeningQuestionActionHandler(state) {
  const newVersion = state.targetingEditVersion + 1;
  const newScreeningQuestions = [];

  const errors = state.errors.filter(
    ({ category }) => category === 'criteria' || category === 'audience',
  );

  return Object.assign({}, state, {
    errors,
    screeningQuestions: newScreeningQuestions,
    targetingEditVersion: newVersion,
  });
}

function updateScreeningQuestionActionHandler(state, action) {
  const newVersion = state.targetingEditVersion + 1;
  const newScreeningQuestions = getScreeningQuestionsAfterUpdate(
    state.screeningQuestions,
    action.payload.question,
  );

  const activeScreeningQuestionIds = newScreeningQuestions.map(
    (question) => `sq-${question.refId}`,
  );
  const errors = state.errors.filter(
    ({ key, category }) =>
      activeScreeningQuestionIds.includes(key) ||
      category === 'criteria' ||
      category === 'audience',
  );

  return Object.assign({}, state, {
    errors,
    screeningQuestions: newScreeningQuestions,
    targetingEditVersion: newVersion,
  });
}

function fetchTargetingSuccessActionHandler(state, action) {
  const {
    audiences = [],
    screeningQuestions = [],
    targetingEditVersion = 1,
    startVersion = 1,
    surveyType = SURVEY_TYPE_ENUM.REGULAR,
    previousStatusName,
    userHideAgeUnderEighteen,
  } = action.data;

  const currentVersion = get(state, 'targetingEditVersion', -1);
  const receivedVersion = get(action, 'data.targetingEditVersion', -1);

  // coming here means that we should not accept what the server gave us, but re-submit what we already have
  // forceAcceptServerVersion bypasses this after discard changes (reloadSurveyState with afterClearRevision)
  if (
    !action.data?.forceAcceptServerVersion &&
    currentVersion > receivedVersion
  ) {
    Raven.captureMessage(
      '[Targeting]: Received version previous than already stored',
    );
    return Object.assign({}, state, {
      isFetching: false,
    });
  }
  const activeAudienceRefId = get(audiences, '[0].refId', null);

  const newAudiences = audiences.map((audience) => {
    const hasResidentialPostalCodes = audience.features.some(
      ({ key }) => key === residentialPostalCodesCriterionKey,
    );

    return {
      ...pick(audience, ['id', 'refId', 'quotaCompletes', 'isTemporary']),
      criteria: initialCriteria.map((criterion) => {
        const activeCriterion = audience.features.find(
          ({ key }) => key === criterion.key,
        );

        const initializedCriterion = setupCriterion(
          activeCriterion,
          userHideAgeUnderEighteen,
        );

        if (
          hasResidentialPostalCodes &&
          geolocationCriteriaKeysWithoutPostalCode.includes(criterion.key)
        ) {
          return {
            ...criterion,
            ...initializedCriterion,
            isEnabled: false,
            isDisabled: true,
            hasQuotas: false,
          };
        }

        if (
          surveyType === SURVEY_TYPE_ENUM.INTERNAL &&
          criterion.key === 'api_key'
        ) {
          const isEnabled = get(initializedCriterion, 'isEnabled', false);
          return {
            ...criterion,
            ...initializedCriterion,
            label: 'Apps',
            isDisablingAllowed: false,
            isEnabled: true,
            isPristine: !isEnabled,
          };
        }
        if (activeCriterion) {
          return {
            ...criterion,
            ...initializedCriterion,
            isEnabled: true,
          };
        }

        return criterion;
      }),
    };
  });

  const newScreeningQuestions = screeningQuestions.map((question) => ({
    ...question,
    pristineValues: defaultPristineValues(false),
  }));

  return Object.assign({}, state, {
    isFetching: false,
    hasFetched: true,
    startVersion,
    previousStatusName,
    targetingEditVersion,
    audiences: adjustAudienceQuotaPercentages(newAudiences),
    activeAudienceRefId,
    screeningQuestions: newScreeningQuestions,
    savedTargetingVersion: targetingEditVersion,
  });
}

export {
  fetchTargetingSuccessActionHandler,
  addScreeningQuestionActionHandler,
  duplicateMultipleScreeningQuestionsHandler,
  removeScreeningQuestionActionHandler,
  removeAllScreeningQuestionActionHandler,
  updateScreeningQuestionActionHandler,
};
